// CP2.3 — a vue-resource-compatible HTTP layer over axios (the already-installed axios; the version
// bump D1 is a later/post-Vue-3 step). Drop-in replacement for `Vue.http` / `this.$http` so the ~65
// call sites and the two interceptors (bad_request, unauthorized) keep working unchanged.
//
// Contract preserved from vue-resource:
//  - call forms: Vue.http(config) · Vue.http.get/delete/head/options(url, options) ·
//    Vue.http.post/put/patch(url, body, options) · same via this.$http
//  - response shape: { body, bodyText, ok, status, statusText, url, headers.get(name) }
//  - REJECTS on non-2xx (and network error → status 0) with that same response shape (call sites use
//    .then(ok).catch(err => err.body/err.status))
//  - interceptors: Vue.http.interceptors is an array of (request, next) fns; each may register a
//    response handler via next(handler); handlers run for EVERY response (2xx and non-2xx), in reverse
//    registration order (vue-resource semantics). The request object exposes
//    { method, url, body, params, headers, crossOrigin, responseType }.
import axios from 'axios'

export const client = axios.create()

var interceptors = []

// vue-resource-style flat param serialization (repeat key for arrays; no "[]") — matches the Django
// REST backend's expectations and vue-resource's prior behaviour (axios 0.x default appends "[]").
function serializeParams (params) {
  var parts = []
  Object.keys(params || {}).forEach(function (key) {
    var val = params[key]
    if (val === null || val === undefined) return
    if (Array.isArray(val)) {
      val.forEach(function (v) { parts.push(encodeURIComponent(key) + '=' + encodeURIComponent(v)) })
    } else {
      parts.push(encodeURIComponent(key) + '=' + encodeURIComponent(val))
    }
  })
  return parts.join('&')
}

function toResponse (axiosRes, url) {
  var data = axiosRes.data
  var headers = axiosRes.headers || {}
  var status = axiosRes.status
  return {
    body: data,
    data: data, // vue-resource Response exposes `.data` as an alias of `.body` (used widely, e.g. authService)
    get bodyText () { return typeof data === 'string' ? data : (data == null ? '' : JSON.stringify(data)) },
    // vue-resource Response methods: .json()/.text() return Promises. The body is already parsed by axios.
    json: function () { return Promise.resolve(data) },
    text: function () { return Promise.resolve(typeof data === 'string' ? data : (data == null ? '' : JSON.stringify(data))) },
    ok: status >= 200 && status < 300,
    status: status,
    statusText: axiosRes.statusText,
    url: url,
    headers: { get: function (name) { return headers[String(name).toLowerCase()] } }
  }
}

// vue-resource surfaces a status-0 response when there is no HTTP response (network error / abort).
function networkErrorResponse (url) {
  return {
    body: '', data: '', bodyText: '', ok: false, status: 0, statusText: '', url: url,
    json: function () { return Promise.resolve('') },
    text: function () { return Promise.resolve('') },
    headers: { get: function () { return undefined } }
  }
}

function runResponseHandlers (handlers, response) {
  for (var i = handlers.length - 1; i >= 0; i--) {
    try { handlers[i](response) } catch (e) { /* a handler must not break the response chain */ }
  }
}

function send (config) {
  config = config || {}
  var url = config.url
  var method = (config.method || 'get').toLowerCase()
  var request = {
    method: method.toUpperCase(),
    url: url,
    body: config.body,
    params: config.params,
    headers: Object.assign({}, config.headers || {}),
    crossOrigin: /^https?:\/\//i.test(url || ''),
    responseType: config.responseType
  }
  var handlers = []
  for (var i = 0; i < interceptors.length; i++) {
    interceptors[i](request, function (handler) {
      if (typeof handler === 'function') handlers.push(handler)
    })
  }
  var axiosConfig = {
    method: request.method,
    url: request.url,
    headers: request.headers,
    params: request.params,
    paramsSerializer: serializeParams,
    responseType: request.responseType,
    // vue-resource RESOLVES the promise for EVERY HTTP response (incl. 4xx/5xx) and lets the app
    // branch on `.ok` (e.g. the money-path `checkStatus` helper rejects on !ok). Match that: accept
    // all statuses here so non-2xx resolves. Only a true network error / no response rejects.
    validateStatus: function () { return true }
  }
  if (request.body !== undefined && request.body !== null) axiosConfig.data = request.body

  return client(axiosConfig).then(function (axiosRes) {
    var response = toResponse(axiosRes, url)
    runResponseHandlers(handlers, response)
    return response
  }, function (err) {
    // reached only on a network error / no response (validateStatus accepts all HTTP statuses above)
    var response = err && err.response ? toResponse(err.response, url) : networkErrorResponse(url)
    runResponseHandlers(handlers, response)
    return Promise.reject(response)
  })
}

function http (config) { return send(config) }
http.interceptors = interceptors
;['get', 'delete', 'head', 'options'].forEach(function (m) {
  http[m] = function (url, options) { return send(Object.assign({}, options, { url: url, method: m })) }
})
;['post', 'put', 'patch'].forEach(function (m) {
  http[m] = function (url, body, options) { return send(Object.assign({}, options, { url: url, method: m, body: body })) }
})
export { http }

export default {
  install: function (app) {
    app.config.globalProperties.$http = http // `this.$http` in components (Vue 3 replacement for Vue.prototype)
  }
}
